import { type DocNode } from '@atlaskit/adf-schema';
import { reduce } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { ROVO_SKILL_COMBINED_EXTENSION_KEY } from '@atlassian/conversation-assistant-content-transformer/utils/skills';

export type ContentFeatures = {
	adfLength: number;
	textLength: number;
	wordCount: number;
	linkCount: number;
	mentionCount: number;
	explicitSkills: string[];
};

const wordCount = (text?: string) => {
	if (text) {
		const matches = text.match(/\S+/g);
		if (matches) {
			return matches.length;
		}
	}
	return 0;
};
const processADFNode = (currentValue: ContentFeatures, node: ADFEntity): ContentFeatures => {
	switch (node.type) {
		case 'text':
			const linkCount = node.marks?.some((mark) => mark.type === 'link')
				? currentValue.linkCount + 1
				: currentValue.linkCount;
			return {
				...currentValue,
				textLength: currentValue.textLength + (node.text ? node.text.length : 0),
				wordCount: currentValue.wordCount + wordCount(node.text),
				linkCount,
			};
		case 'mention':
			return {
				...currentValue,
				mentionCount: currentValue.mentionCount + 1,
			};
		case 'inlineCard':
		case 'embedCard':
			return {
				...currentValue,
				linkCount: currentValue.linkCount + 1,
			};
		case 'inlineExtension':
			if (
				node.attrs?.extensionKey === ROVO_SKILL_COMBINED_EXTENSION_KEY &&
				node.attrs?.parameters?.slug
			) {
				return {
					...currentValue,
					explicitSkills: [...currentValue.explicitSkills, node.attrs.parameters.slug],
				};
			}
			return currentValue;
	}
	return currentValue;
};

export const extractContentFeatures = (adf: DocNode): ContentFeatures => {
	const initialFeatures: ContentFeatures = {
		adfLength: JSON.stringify(adf).length,
		textLength: 0,
		wordCount: 0,
		linkCount: 0,
		mentionCount: 0,
		explicitSkills: [],
	};

	return reduce<ContentFeatures>(
		adf,
		(currentValue, node: ADFEntity) => processADFNode(currentValue, node),
		initialFeatures,
	);
};
